/**
 * [04]-STUDIOS™ / ABOUT × home hero scroll choreography (V2).
 * Seven real photographs, native scroll, no photo animation.
 * The ONLY motion is copy/CTA ghost, the first scroll cue and 04 Assist reveal.
 */
(() => {
  'use strict';
  const mounted = new WeakMap();
  const clamp = v => Math.min(1, Math.max(0, v));

  class AboutScroll {
    constructor(root) {
      this.root = root;
      this.chapters = [...root.querySelectorAll('[data-a04-chapter]')];
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.abort = new AbortController();
      this.frame = 0;
      this.last = new WeakMap();
      const signal = this.abort.signal;
      const schedule = () => this.schedule();
      root.addEventListener('click', event => {
        const cue = event.target.closest?.('.a04-story__scroll-cue');
        if (!cue || !root.contains(cue)) return;
        const chapter = cue.closest('[data-a04-chapter]');
        const index = this.chapters.indexOf(chapter);
        const target = this.chapters[index + 1] || root.querySelector('.a04-story__handoff');
        if (index < 0 || !target) return;
        target.scrollIntoView({behavior: this.reduced.matches ? 'auto' : 'smooth', block: 'start'});
      }, {signal});
      window.addEventListener('scroll', schedule, {passive:true, signal});
      // Some [04] layouts have their own native .page-wrapper scrollport.
      root.closest('.page-wrapper')?.addEventListener('scroll', schedule, {passive:true, signal});
      document.addEventListener('scroll', schedule, {passive:true, capture:true, signal});
      window.addEventListener('resize', schedule, {passive:true, signal});
      window.addEventListener('pageshow', schedule, {signal});
      this.reduced.addEventListener?.('change', schedule, {signal});
      this.render();
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.render();
      });
    }

    render() {
      const vh = Math.max(1, window.innerHeight);
      const reduce = this.reduced.matches;
      let firstExit = 1;

      for (let i = 0; i < this.chapters.length; i++) {
        const chapter = this.chapters[i];
        const bounds = chapter.getBoundingClientRect();

        // EXACT same model as the Calm Hoodie hero:
        // progressive rAF opacity + a small downward movement.
        // Each chapter's own scroll geometry controls the effect.
        // Next image moves into view naturally, without a crossfade.
        // One viewport per photo: reveal the copy as its scene arrives,
        // then ghost it while the next scene naturally enters the viewport.
        // This removes the old 155svh sticky pause and works in both directions.
        const entering = clamp((vh * .88 - bounds.top) / (vh * .50));
        const leaving = clamp((bounds.bottom - vh * .28) / (vh * .50));
        const visibility = reduce ? 1 : Math.min(entering, leaving);
        const opacity = visibility.toFixed(3);
        const shift = reduce ? '0px' : ((1-visibility) * 24).toFixed(1)+'px';
        const click = visibility > .8 ? 'auto' : 'none';
        const key = opacity+'/'+shift+'/'+click;

        if (this.last.get(chapter) !== key) {
          this.last.set(chapter, key);
          chapter.style.setProperty('--a04-copy-opacity',opacity);
          chapter.style.setProperty('--a04-copy-shift',shift);
          chapter.style.setProperty('--a04-copy-pointer',click);
          const copy = chapter.querySelector('.a04-story__content');
          if (copy) {
            copy.setAttribute('aria-hidden',visibility <= .03?'true':'false');
            copy.querySelectorAll('a').forEach(a => {
              if (visibility <= .8) a.setAttribute('tabindex','-1');
              else a.removeAttribute('tabindex');
            });
          }
        }

        if (i === 0) firstExit = leaving;
        // A cue on every story; hide it during the first short part of that story's exit.
        const cueFade = reduce ? 1 : 1-clamp(-bounds.top/Math.max(140,Math.min(vh*.18,240)));
        chapter.style.setProperty('--a04-cue-opacity',cueFade.toFixed(3));
        chapter.style.setProperty('--a04-cue-pointer',cueFade > .15 ? 'auto' : 'none');
        const cue = chapter.querySelector('.a04-story__scroll-cue');
        if (cue) cue.tabIndex = cueFade > .15 ? 0 : -1;
      }

      this.root.classList.add('a04-story--motion-ready');
      const html = document.documentElement;

      // Match the homepage Assist behavior: come UP from the bottom as the
      // first headline becomes half ghosted, then stay visible after intro.
      const assist = clamp((.68-firstExit)/.55);
      html.style.setProperty('--a04-assist-opacity',assist.toFixed(3));
      html.style.setProperty('--a04-assist-offset',Math.round((1-assist)*170)+'px');
      html.setAttribute('data-a04-assist',assist >= .85 ? 'ready' : 'waiting');
    }

    destroy() {
      this.abort.abort();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.root.classList.remove('a04-story--motion-ready');
      for (const chapter of this.chapters) {
        chapter.style.removeProperty('--a04-copy-opacity');
        chapter.style.removeProperty('--a04-copy-shift');
        chapter.style.removeProperty('--a04-copy-pointer');
        chapter.style.removeProperty('--a04-cue-opacity');
        chapter.style.removeProperty('--a04-cue-pointer');
        chapter.querySelector('.a04-story__scroll-cue')?.removeAttribute('tabindex');
        chapter.querySelector('.a04-story__content')?.removeAttribute('aria-hidden');
      }
      const html = document.documentElement;
      html.removeAttribute('data-a04-assist');
      html.style.removeProperty('--a04-assist-opacity');
      html.style.removeProperty('--a04-assist-offset');
      mounted.delete(this.root);
    }
  }

  function mount(scope) {
    const roots = [
      ...(scope.matches?.('[data-a04-story]') ? [scope] : []),
      ...scope.querySelectorAll('[data-a04-story]')
    ];
    for (const root of roots) {
      if (!mounted.has(root)) mounted.set(root,new AboutScroll(root));
    }
  }
  function unmount(scope) {
    if (scope.matches?.('[data-a04-story]')) mounted.get(scope)?.destroy();
    scope.querySelectorAll('[data-a04-story]').forEach(root => mounted.get(root)?.destroy());
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(document), {once:true});
  } else mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => unmount(event.target));
})();